import { useEffect, useState } from "react";
import { ArrowUpRight, CalendarDays, MapPin, Star, Ticket } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Reveal, SectionTag } from "@/components/Reveal";
import { getEvents } from "@/lib/site";

const MONTHS = [
  "januar", "februar", "marts", "april", "maj", "juni",
  "juli", "august", "september", "oktober", "november", "december",
];

export const formatEventDate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return `${d}. ${MONTHS[m - 1]} ${y}`;
};

const isPast = (iso) => new Date(iso) < new Date(new Date().toDateString());

export default function MessekalenderPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);

  useEffect(() => {
    getEvents().then(setEvents).catch(() => {});
  }, []);

  return (
    <div data-testid="messekalender-page" className="px-6 pb-24 pt-36 lg:px-16 lg:pb-36">
      <Reveal>
        <SectionTag index="10" label="Messekalender" />
        <h1 className="mt-6 max-w-3xl font-serif text-4xl tracking-tight text-cream sm:text-5xl lg:text-6xl">
          Kommende <span className="italic text-pale">krybdyrmesser</span>
        </h1>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-sage sm:text-lg">
          Her samler vi de kommende reptil- og terrariemesser i Danmark — med dato, sted
          og entré. Kig efter mærket <span className="text-pale">Mød os her</span> — der
          finder du os ved vores egen stand.
        </p>
      </Reveal>

      <div className="mt-20">
        {events.map((e, i) => (
          <Reveal key={e.id} delay={i * 0.05}>
            <div
              data-testid={`messe-event-${i}`}
              className={`grid grid-cols-1 gap-6 border-t border-white/10 py-12 lg:grid-cols-12 ${
                isPast(e.date) ? "opacity-40" : ""
              }`}
            >
              <span className="flex items-center gap-4 font-mono text-xs tracking-[0.25em] text-forest lg:col-span-3">
                <CalendarDays className="h-4 w-4 text-forest/70" strokeWidth={1.5} />
                {formatEventDate(e.date)}
              </span>
              <div className="lg:col-span-5">
                <h2 className="flex flex-wrap items-center gap-3 font-serif text-3xl italic tracking-tight text-cream">
                  {e.name}
                  {e.attending && !isPast(e.date) && (
                    <span
                      data-testid={`messe-badge-${i}`}
                      className="flex items-center gap-1.5 rounded-full border border-forest bg-forest/20 px-3 py-1 font-mono text-[10px] uppercase not-italic tracking-[0.2em] text-pale"
                    >
                      <Star className="h-3 w-3 fill-pale text-pale" /> Mød os her
                    </span>
                  )}
                </h2>
                <a
                  data-testid={`messe-map-link-${i}`}
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(e.location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-2 text-sm text-sage underline-offset-4 transition-colors hover:text-cream hover:underline"
                >
                  <MapPin className="h-4 w-4 text-pale/60" strokeWidth={1.5} />
                  {e.location}
                </a>
              </div>
              {e.entrance && (
                <p className="flex items-start gap-2 text-sm leading-relaxed text-sage lg:col-span-4">
                  <Ticket className="mt-0.5 h-4 w-4 shrink-0 text-pale/60" strokeWidth={1.5} />
                  {e.entrance}
                </p>
              )}
            </div>
          </Reveal>
        ))}
        {events.length === 0 && (
          <p className="border-t border-white/10 py-12 text-sage">
            Ingen messer på kalenderen lige nu — nye datoer annonceres løbende.
          </p>
        )}
      </div>

      <Reveal>
        <p className="mt-4 max-w-2xl font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-sage/70">
          Nye datoer annonceres af arrangørerne — se fx{" "}
          <a
            data-testid="messe-source-link"
            href="https://reptilmesser.dk"
            target="_blank"
            rel="noopener noreferrer"
            className="text-pale underline underline-offset-4 transition-colors hover:text-cream"
          >
            reptilmesser.dk
          </a>
        </p>
      </Reveal>

      <Reveal>
        <div className="mt-10 flex flex-wrap items-center justify-between gap-6 border border-forest/40 bg-forest/10 p-8">
          <p className="max-w-xl font-serif text-2xl italic leading-snug text-cream">
            Skal vi mødes til messen? Skriv til os — så kan vi aftale afhentning af din
            gekko på dagen.
          </p>
          <button
            data-testid="messe-kontakt-button"
            onClick={() => navigate("/kontakt")}
            className="group flex items-center gap-3 rounded-full bg-forest px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-pale hover:text-ink"
          >
            Kontakt os
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </button>
        </div>
      </Reveal>
    </div>
  );
}
